<!-- eslint-disable vue/require-v-for-key -->
<template>
  <div>
    <b-card-actions
      title="Filters"
      action-collapse
      collapsed
    >
      <b-row>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Merchant"
            label-for="merchant_name"
          >
            <b-form-input
              id="merchant_name"
              v-model="filters['name']"
              placeholder="Search"
              autocomplete="nope"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <!-- Apply and Clear Filters -->
      <b-row class="mt-1">
        <b-col>
          <filter-buttons
            :filter-data="filters"
            :apply-filters="applyFilters"
            :clear-filters="clearFilters"
          />
        </b-col>
      </b-row>
    </b-card-actions>
    <b-card no-body>
      <div class="mx-2 mt-2 mb-1">
        <b-row class="d-sm-flex align-items-start">
          <b-col class="d-flex flex-wrap justify-content-end align-items-center mb-1 mb-md-0">
            <ReportTableButtons
              :items="rows"
              :json_fieldz="json_fields"
              :name="`Merchant-Order-Average - Page ${page}.xls`"
              :bulkname="`Merchant-Order-Average.xls`"
              :fetch="fetchAgingMerchantNoPagination"
              @refresh="onClickRefresh"
            />
          </b-col>
        </b-row>
      </div>

      <!-- table -->
      <b-overlay
        id="overlay-background"
        variant="light"
        opacity="0.30"
        blur="10px"
        rounded="sm"
      >
        <b-table
          striped
          hover
          responsive
          :per-page="perPage"
          :items="rows"
          :fields="fields"
          show-empty
          sticky-header="100vh"
        >
          <template #empty>
            <TableDataFetching
              :rows="rows"
              :data-loading="dataLoading"
            />
          </template>
          <template #cell(average)="data">
            <div :class="{'negative-average': data.item.average < 0}">
              {{ data.item.average.toFixed(2) }}%
            </div>
          </template>
          <template #cell(order_count)="data">
            <div>
              {{ data.item.order_count.toLocaleString() }}
            </div>
          </template>
          <template
            v-for="date in dateRange"
            :slot="`cell(${date})`"
            slot-scope="data"
          >
            <div :class="{'order-cell': data.item.orders[date] > 0}">
              {{ data.item.orders[date] }}
            </div>
          </template>
        </b-table>
      </b-overlay>
      <div class="mx-2 mb-2">
        <b-row cols="12">
          <b-col
            class="d-flex align-items-center justify-content-sm-start"
            md="3"
          >
            <label class="width-75">Per page</label>
            <v-select
              v-model="perPage"
              :options="pageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block width-125"
            />
          </b-col>
          <b-col
            md="9"
            class="d-flex align-items-center justify-content-end"
          >
            <span class="text-muted mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
            <b-pagination
              v-model="page"
              :per-page="perPage"
              :total-rows="(meta.total)? meta.total : 0"
              class="mb-0 mt-1 mt-sm-0"
              first-number
              last-number
              next-class="next-item"
              prev-class="prev-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCol,
  BPagination,
  BRow,
  BOverlay,
  VBModal,
  BTable,
  BFormGroup,
  BFormInput,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import FilterButtons from '@/components/Filter/FilterButtons.vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import Filter from '@core/mixins/filter'
import ErrorMessage from '@core/mixins/ErrorMessage'

const orderRepository = RepositoryFactory.get('order')

export default {
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  components: {
    BPagination,
    BCard,
    BRow,
    BCol,
    BTable,
    BOverlay,
    vSelect,
    FilterButtons,
    BCardActions,
    BFormGroup,
    BFormInput,
  },
  mixins: [Filter, ErrorMessage],
  data() {
    return {
      perPage: 20,
      pageOptions: [10, 20, 50, 100, 200],
      page: 1,
      fields: [],
      dateRange: [],
      filters: {},
      total: 0,
      meta: {},
      totalRows: 1,
      dataLoading: false,
      rows: [],
    }
  },
  computed: {
    json_fields() {
      return {
        Merchant: {
          field: 'merchant_name',
          callback: value => `"${value}"`,
        },
        Average: {
          field: 'average',
          callback: value => `"${value}%"`,
        },
        'Total Order Count': {
          field: 'total_order_count',
          callback: value => `"${value}"`,
        },
        // Dynamically add date columns for each day in dateRange
        ...this.generateDateFieldsForCSV(),
      }
    },
  },
  watch: {
    options: {
      handler() {
        this.fetchMerchantAgingReportData()
      },
    },
    page() {
      this.fetchMerchantAgingReportData()
    },
    perPage() {
      this.fetchMerchantAgingReportData()
    },
  },
  created() {
    this.averageGroup = this.$route.query.average
    this.fetchMerchantAgingReportData()
  },
  methods: {
    async fetchMerchantAgingReportData() {
      this.dataLoading = true
      try {
        // eslint-disable-next-line no-underscore-dangle
        this.filterQuery = this.__refineDateQuery(this.filterQuery, [this.filters])
        const { data } = await orderRepository.fetchMerchantAgingReportData(this.averageGroup, this.filterQuery, this.page, this.perPage)

        this.dateRange = data.dateRange
        this.meta = data.meta
        // Dynamically generate the fields based on the date range
        this.fields = [
          { key: 'merchant_name', label: 'Merchant', class: 'text-left' },
          { key: 'average', label: 'Average', class: 'text-right' },
          { key: 'total_order_count', label: 'Total Order Count', class: 'text-right' },
          ...this.generateDateFields(),
        ]

        // Process the rows to include total order counts and ensure date-wise orders
        this.rows = Object.values(data.data).map(merchant => ({
          ...merchant,
          total_order_count: Object.values(merchant.orders).reduce((sum, count) => sum + count, 0).toLocaleString(),
          orders: this.ensureAllDatesPresent(merchant.orders, this.dateRange),
        }))
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async fetchAgingMerchantNoPagination() {
      try {
        // eslint-disable-next-line no-underscore-dangle
        this.filterQuery = this.__refineDateQuery(this.filterQuery, [this.filters])
        const { data } = (await orderRepository.fetchAgingMerchantNoPagination(this.averageGroup, this.filterQuery))
        this.dateRange = data.dateRange
        const downloadAll = Object.values(data.data).map(merchant => ({
          ...merchant,
          total_order_count: Object.values(merchant.orders).reduce((sum, count) => sum + count, 0).toLocaleString(),
          orders: this.ensureAllDatesPresent(merchant.orders, this.dateRange),
        }))
        return downloadAll
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    generateDateFields() {
      return this.dateRange.map(date => ({
        key: date,
        label: this.formatDate(date),
        class: 'text-right',
      }))
    },

    formatDate(dateStr) {
      const date = new Date(dateStr)
      const day = date.getDate()
      const suffix = this.getDateSuffix(day)
      const month = date.toLocaleString('en-US', { month: 'long' })
      return `${day}${suffix} ${month}`
    },

    getDateSuffix(day) {
      if (day > 3 && day < 21) return 'th'
      switch (day % 10) {
        case 1:
          return 'st'
        case 2:
          return 'nd'
        case 3:
          return 'rd'
        default:
          return 'th'
      }
    },

    ensureAllDatesPresent(orders, dateRange) {
      // Ensure every date in dateRange has an entry in orders, defaulting to 0 if missing
      const filledOrders = {}
      dateRange.forEach(date => {
        filledOrders[date] = (orders[date] || 0).toLocaleString()
      })
      return filledOrders
    },
    applyFilters() {
      this.fetchMerchantAgingReportData()
    },
    clearFilters() {
      this.filters = {}
      this.$nextTick(() => {
        this.fetchMerchantAgingReportData()
      })
    },
    onClickRefresh() {
      this.fetchMerchantAgingReportData()
    },
    handleChangePage(page) {
      this.page = page
      this.fetchMerchantAgingReportData()
    },
    changePage(value) {
      this.perPage = value
    },
    generateDateFieldsForCSV() {
      const dateFields = {}
      this.dateRange.forEach(date => {
        dateFields[date] = {
          field: `orders.${date}`, // Path to the orders object for the specific date
          callback: value => `"${value}"`, // Ensure that values are quoted
        }
      })
      return dateFields
    },
  },
}
</script>

<style lang="scss">
@import '../@core/scss/vue/libs/vue-select.scss';

.color-box {
  padding: 8px;
  color: #fff;
  border-radius: 4px;
  text-align: center;
  width: 100px;
}
</style>
